/**
 * pedal-disambiguation.js
 * -----------------------
 * Shared (reusable) module that, for specific (model, body) combinations,
 * asks the user whether their gas pedal is METAL or PLASTIC and picks the
 * matching socket (PC code) based on the answer.
 *
 * - Rules live here, in PEDAL_DISAMBIG_CONFIG (no separate JSON / no fetch).
 * - Images are pre-rendered on the Liquid side in #pedalDisambigImageBank
 *   (pedal-disambiguation-step.liquid) using the native `images[...]` object
 *   + image_tag; this file only clones the right image from that bank.
 * - The market code ALWAYS comes from the caller (variant.market / plug prefix),
 *   so it is dynamic and never hardcoded anywhere in this file.
 * - The content of the "Still not sure?" modal is also generated from the
 *   config (option.help).
 *
 * TO ADD A NEW RULE:
 *   1) Add the model/body/prompt/options to PEDAL_DISAMBIG_CONFIG below
 *   2) Make sure ALL image_keys you use (pedal + plug) also exist in the image
 *      bank of pedal-disambiguation-step.liquid (as an {% assign %} + span block)
 *   3) If socket_suffix DIFFERS between markets, add a "markets" override to
 *      that body (see the commented example below)
 *
 * MODAL (option.help) FIELDS:
 *   plug_image_key : key of the plug image in the image bank
 *   text           : description shown under the images in the modal
 *   (The yellow circle marking on the plug images is part of the image itself;
 *    no marker is drawn in code.)
 */

window.PC = window.PC || {};

(function () {
  const PEDAL_DISAMBIG_CONFIG = {
    Outback: {
      "BT (2020-2025)": {
        prompt: "Please check your gas pedal itself.",
        help_button_label: "Still not sure?",
        help_select_label: "This is my pedal",
        options: [
          {
            label: "I have a METAL gas pedal and lever.",
            image_key: "outback-PC63",
            socket_suffix: "PC63",
            spec_badge: "Non-US Spec",
            help: {
              plug_image_key: "PC63-plugpicture",
              text: "Non-US Subaru Outbacks come with a metal pedal and pedal lever, and the pedal position sensor plug has a small notch.",
            },
          },
          {
            label: "I have a PLASTIC gas pedal and lever.",
            image_key: "outback-PC55",
            socket_suffix: "PC55",
            spec_badge: "US Spec",
            help: {
              plug_image_key: "PC55-plugpicture",
              text: "US market Subaru Outbacks come with a plastic pedal and lever, and the pedal position sensor plug does not have a notch.",
            },
          },
        ],
        // Example override if the suffixes differ in another market:
        // markets: {
        //   UK: {
        //     prompt: "...",
        //     options: [ ... ],
        //   },
        // },
      },
    },
  };

  function resolveRule(model, body, marketCode) {
    const rule = PEDAL_DISAMBIG_CONFIG[model] && PEDAL_DISAMBIG_CONFIG[model][body];
    if (!rule) return null;
    if (rule.markets && marketCode && rule.markets[marketCode]) {
      return rule.markets[marketCode];
    }
    return rule;
  }

  // Get the image markup from the image bank (rendered by Liquid via image_tag)
  function getBankImage(imageKey) {
    const bankItem = document.querySelector(
      `#pedalDisambigImageBank [data-pedal-image-key="${imageKey}"]`
    );
    if (!bankItem) {
      console.error(`Image not found: image_key="${imageKey}" - was it added to the image bank?`);
      return "";
    }
    return bankItem.innerHTML;
  }

  /* ------------------------------------------------------------------
   * "Still not sure?" MODAL
   * ------------------------------------------------------------------ */
  function closeHelpModal() {
    const modal = document.querySelector("#pedalHelpModal");
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove("pedal-help-open");
  }

  function getHelpModal() {
    const modal = document.querySelector("#pedalHelpModal");
    if (!modal) {
      console.error("#pedalHelpModal not found - was the snippet rendered?");
      return null;
    }

    if (!modal.dataset.bound) {
      // Move to <body> so theme overflow / transform rules cannot affect it
      document.body.appendChild(modal);

      modal.addEventListener("click", (e) => {
        if (e.target.closest("[data-pedal-help-close]")) closeHelpModal();
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && !modal.hidden) closeHelpModal();
      });

      modal.dataset.bound = "true";
    }
    return modal;
  }

  function openHelpModal(rule, marketCode, onChoose) {
    const modal = getHelpModal();
    if (!modal) return;

    const content = modal.querySelector(".pedal-help-modal__content");
    content.innerHTML = "";

    rule.options
      .filter((opt) => opt.help)
      .forEach((opt) => {
        const col = document.createElement("div");
        col.className = "pedal-help-col";
        col.innerHTML = `
          <p class="pedal-help-col__title">${opt.label}</p>
          <div class="pedal-help-images">
            <div class="pedal-help-pedal">${getBankImage(opt.image_key)}</div>
            <div class="pedal-help-plug">${getBankImage(opt.help.plug_image_key)}</div>
          </div>
          <p class="pedal-help-col__text">${opt.help.text}</p>
          <button type="button" class="pedal-help-col__select">${rule.help_select_label || "Select"}</button>
        `;

        // Selecting from the modal follows the same flow as clicking a card
        col.querySelector(".pedal-help-col__select").addEventListener("click", () => {
          onChoose(`${marketCode}-${opt.socket_suffix}`);
        });

        content.appendChild(col);
      });

    modal.hidden = false;
    document.documentElement.classList.add("pedal-help-open");
  }

  /* ------------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------------ */

  /**
   * Checks whether a disambiguation rule exists for the given model + body + market.
   * Returns null if not (the normal ATC flow continues unchanged).
   * Synchronous - there is no fetch, so no await is needed.
   */
  window.PC.getPedalDisambiguation = function (model, body, marketCode) {
    return resolveRule(model, body, marketCode);
  };

  /**
   * Renders the disambiguation UI. When the user picks an option,
   * onSelect(chosenPlug) is called; e.g. chosenPlug = "DE-PC63".
   * marketCode is always the value passed in by the caller (variant.market) -
   * it is never hardcoded here.
   */
  window.PC.renderPedalDisambiguation = function (rule, marketCode, onSelect) {
    const container = document.querySelector("#pedalDisambiguationStep");
    if (!container) {
      console.error("#pedalDisambiguationStep not found - was the snippet rendered?");
      return;
    }

    const resolvedMarket = marketCode || window.PC_DISAMBIG_MARKET_FALLBACK;
    if (!marketCode) {
      console.warn("variant.market was empty, fell back to the page market (" + resolvedMarket + ").");
    }

    container.innerHTML = "";
    container.style.display = "block";

    if (rule.prompt) {
      const prompt = document.createElement("p");
      prompt.className = "pedal-disambig-prompt";
      prompt.textContent = rule.prompt;
      container.appendChild(prompt);
    }

    const optionsWrap = document.createElement("div");
    optionsWrap.className = "pedal-disambig-options";
    container.appendChild(optionsWrap);

    // Cards AND the modal share the same flow: close modal, hide step, run onSelect
    const choose = (plug) => {
      closeHelpModal();
      container.style.display = "none";
      container.innerHTML = "";
      onSelect(plug);
    };

    rule.options.forEach((opt) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pedal-disambig-option";
      btn.dataset.plug = `${resolvedMarket}-${opt.socket_suffix}`;

      btn.innerHTML = `
        ${getBankImage(opt.image_key)}
        <span class="pedal-disambig-label">${opt.label}</span>
        ${opt.spec_badge ? `<span class="pedal-disambig-badge">${opt.spec_badge}</span>` : ""}
      `;

      btn.addEventListener("click", () => choose(btn.dataset.plug));

      optionsWrap.appendChild(btn);
    });

    // "Still not sure?" - a single centered button under the two cards
    if (rule.help_button_label && rule.options.some((opt) => opt.help)) {
      const helpWrap = document.createElement("div");
      helpWrap.className = "pedal-disambig-help";

      const helpBtn = document.createElement("button");
      helpBtn.type = "button";
      helpBtn.className = "pedal-disambig-help-btn";
      helpBtn.textContent = rule.help_button_label;
      helpBtn.addEventListener("click", () => openHelpModal(rule, resolvedMarket, choose));

      helpWrap.appendChild(helpBtn);
      container.appendChild(helpWrap);
    }
  };
})();